import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Drawer, Tabs, Descriptions, Tag } from 'antd';
import { PaperClipOutlined, AuditOutlined, ProfileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useStore } from '../../../stores/root-store-context';
import { FinancialTransaction } from '../../../api/FinanceApiClient';
import OperationAttachments from './attachments/OperationAttachments';
import ErpHistoryPanel from './history/ErpHistoryPanel';

const { TabPane } = Tabs;

export interface OperationDetailDrawerProps {
  transaction: FinancialTransaction | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Read-only detail view for an Operation, opened by clicking its row (mirrors CRM's record detail).
 * Three tabs: Details (the record's fields), Files (the attachments — list / download / upload /
 * delete, the place they are finally viewable outside the edit modal) and History (the versioned
 * change log). Editing stays on the row's Edit button. Inactive tabs are destroyed so Files/History
 * fetch only when their tab is opened.
 */
const OperationDetailDrawer: React.FC<OperationDetailDrawerProps> = observer(({ transaction, open, onClose }) => {
  const { t } = useTranslation();
  const { financeStore } = useStore();
  const [activeTab, setActiveTab] = useState<string>('details');

  const op = transaction ? financeStore.operationTypes.find(o => o.id === transaction.operationTypeId) : null;
  const cat = op ? financeStore.operationCategories.find(c => c.id === op.operationCategoryId) : null;
  const cur = transaction ? financeStore.currencies.find(c => c.id === transaction.currencyId) : null;
  const pay = transaction ? financeStore.paymentTypes.find(p => p.id === transaction.paymentTypeId) : null;
  const isIncome = cat?.id === 1;

  return (
    <Drawer
      title={transaction?.name || t('Details')}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      afterOpenChange={visible => {
        if (!visible) setActiveTab('details');
      }}
    >
      {transaction && (
        <Tabs activeKey={activeTab} onChange={setActiveTab} destroyInactiveTabPane>
          <TabPane
            tab={
              <span>
                <ProfileOutlined /> {t('Details')}
              </span>
            }
            key="details"
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('Name')}>{transaction.name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('Date')}>
                {transaction.date ? dayjs(transaction.date).format('YYYY-MM-DD') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Type')}>
                <Tag color={isIncome ? 'green' : 'red'}>{isIncome ? t('Income') : t('Expense')}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Amount')}>
                <span style={{ color: isIncome ? '#3f8600' : '#cf1322', fontWeight: 'bold' }}>
                  {isIncome ? '+' : '-'}
                  {cur?.symbol || ''}
                  {(transaction.amount || 0).toFixed(2)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label={t('Currency')}>
                {cur ? `${cur.name || ''} (${cur.shortName || ''})`.trim() : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Operation')}>{op?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('Payment')}>{pay?.name ? t(pay.name) : '—'}</Descriptions.Item>
              <Descriptions.Item label={t('Description')}>{transaction.description || '—'}</Descriptions.Item>
            </Descriptions>
          </TabPane>

          <TabPane
            tab={
              <span>
                <PaperClipOutlined /> {t('Files')}
              </span>
            }
            key="files"
          >
            <OperationAttachments transactionId={transaction.id} />
          </TabPane>

          <TabPane
            tab={
              <span>
                <AuditOutlined /> {t('ErpHistory')}
              </span>
            }
            key="history"
          >
            <ErpHistoryPanel entityType="FinancialTransaction" entityId={transaction.id} active={open} />
          </TabPane>
        </Tabs>
      )}
    </Drawer>
  );
});

export default OperationDetailDrawer;
